import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, FileSpreadsheet, FolderOpen } from 'lucide-react';
import { IntegrationCard } from './IntegrationCard';

interface DropboxFile {
  id: string;
  name: string;
  size?: number;
  modified?: string;
  path?: string;
}

export const DropboxSheetsIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [files, setFiles] = useState<DropboxFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setChecking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('dropbox_tokens')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      setIsConnected(!!data);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Ask the dropbox-auth edge function for an authorization URL,
      // using the user's id as the OAuth `state` so the callback can
      // identify the user when Dropbox redirects back.
      const { data, error } = await supabase.functions.invoke('dropbox-auth', {
        body: { action: 'get_auth_url', userId: session.user.id },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to initialize Dropbox OAuth');
      }

      if (data?.authUrl) {
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL received from Dropbox');
      }
    } catch (error: any) {
      console.error('Dropbox OAuth error:', error);
      toast.error(error.message || 'Failed to start Dropbox OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadFiles = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke("dropbox-files", {
        body: { path: "" },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message);
      if (!data?.success) {
        toast.error(data?.error || "Failed to load Dropbox files");
        if ((data?.error || "").toLowerCase().includes("not connected")) {
          setIsConnected(false);
        }
        return;
      }

      setFiles(data.files || []);
      setIsConnected(true);
      toast.success(`Loaded ${data.count} real files from Dropbox`);
    } catch (error: any) {
      console.error("Error loading files:", error);
      toast.error(error.message || "Failed to load files from Dropbox");
    } finally {
      setIsLoading(false);
    }
  };


  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with Dropbox + Google Sheets data...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'dropbox_sheets',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow with Dropbox + Google Sheets started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('dropbox_tokens')
        .delete()
        .eq('user_id', user.id);

      setIsConnected(false);
      setFiles([]);
      toast.success('Disconnected from Dropbox');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from Dropbox');
    }
  };

  return (
    <IntegrationCard
      icon={
        <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
          <FolderOpen className="w-4 h-4 text-white" />
        </div>
      }
      name="Dropbox"
      description="Sign in with your own Dropbox account so Veylo can read the spreadsheets you store there."
      isConnected={isConnected}
      checking={checking}
      isConnecting={isConnecting}
      onConnect={handleOAuthConnect}
      onDisconnect={disconnect}
      actions={
        <>
          <Button onClick={loadFiles} disabled={isLoading} variant="outline">
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</>
            ) : (
              'Load Files'
            )}
          </Button>
          <Button onClick={runAIWorkflow} variant="outline">Run AI Workflow</Button>
        </>
      }
    >
      {files.length > 0 ? (
        <div>
          <h4 className="font-medium mb-2">Files ({files.length})</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {files.map((file, index) => (
              <div key={file.id || index} className="p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-green-600" />
                  <div className="font-medium">{file.name}</div>
                </div>
                {file.size !== undefined && (
                  <div className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        !isLoading && (
          <p className="text-sm text-muted-foreground">
            No files loaded yet. Choose "Load Files" to read your Dropbox.
          </p>
        )
      )}
    </IntegrationCard>
  );
};
